/**
 * 在庫管理ページのテーブルに関するサービス
 */
export class InventoryTableService {
  /**
   * ソート可能なトランザクションキーの一覧
   * (DBに直接対応するカラムがあるもののみ)
   */
  private static SORTABLE_TRANSACTION_KEYS = new Set([
    'nowInventory',
    'endInventory',
    'arrival',
    'expectedArrival',
    'salesResult',
    'announce',
    'supplierAnnounce',
  ]);

  /**
   * トランザクションキーからバックエンドのカラム名へのマッピング
   */
  private static TRANSACTION_SORT_COLUMN_MAP: Record<string, string> = {
    nowInventory: 'inventoryAmount',
    endInventory: 'inventoryAmount',
    arrival: 'arrivalAmount',
    expectedArrival: 'arrivalAmount',
    salesResult: 'salesResultAmountTotal',
    announce: 'customerForecastAmountTotal',
    supplierAnnounce: 'supplierForecastAmount',
  };

  /**
   * ヘッダーキーがソート可能かどうかを判別する関数
   * @param key - 判別したいヘッダーキー
   * @returns - ヘッダーキーがソート可能かどうか
   */
  public static isHeaderKeySortable = (key: string): boolean => {
    // トランザクションキー（transaction-{offset}-{type}）の場合
    if (InventoryTableService.isTransactionSortKey(key)) {
      const transactionType = InventoryTableService.extractTransactionType(key);
      return InventoryTableService.SORTABLE_TRANSACTION_KEYS.has(transactionType);
    }

    return [
      'warehouseNumber',
      'warehouse',
      'supplierNumber',
      'supplier',
      'businessNumber',
      'businessName',
      'package',
      'unit',
      'manufacturer',
      'leadTime',
      // TODO: 標準在庫指数は単位(Month/Week)を考慮せずにソートされてしまうため、BE側で日数変換の対応が必要
      // 'stdInventoryIndex',
      'MOQ',
      'supplierForecastResponsibleUser',
    ].includes(key);
  };

  /**
   * ヘッダーキーをAPIのパラメータに変換する関数
   * @param key - ヘッダーキー
   * @returns - ヘッダーキーに対応するパラメータ
   */
  public static convertHeaderKeyToParameter(key: string) {
    switch (key) {
      case 'supplier':
        return 'supplierName';
      case 'MOQ':
        return 'moq';
      case 'supplierForecastResponsibleUser':
        return 'aggregateBusinessResponsibleUser';
      default:
        return key;
    }
  }

  /**
   * ヘッダーキーがトランザクション系のソートキーかどうかを判別する
   * @param key - ヘッダーキー（例: "transaction--2-arrival"）
   * @returns - トランザクションキーかどうか
   */
  public static isTransactionSortKey(key: string): boolean {
    return key.startsWith('transaction-');
  }

  /**
   * トランザクションヘッダーキーからトランザクションタイプを抽出する
   * @param key - ヘッダーキー（例: "transaction--2-arrival" or "transaction-3-salesResult"）
   * @returns - トランザクションタイプ（例: "arrival", "salesResult"）
   */
  private static extractTransactionType(key: string): string {
    // key format: "transaction-{offset}-{type}"
    // offset can be negative (e.g., "-2"), so split from the last "-"
    const parts = key.split('-');
    // parts for "transaction--2-arrival" = ["transaction", "", "2", "arrival"]
    // parts for "transaction-3-salesResult" = ["transaction", "3", "salesResult"]
    // The type is always the last element
    return parts[parts.length - 1];
  }

  /**
   * トランザクションヘッダーキーから月オフセットを抽出する
   * @param key - ヘッダーキー（例: "transaction--2-arrival"）
   * @returns - 月オフセット（例: -2）
   */
  private static extractMonthOffset(key: string): number {
    // key format: "transaction-{offset}-{type}"
    // Remove "transaction-" prefix
    const withoutPrefix = key.substring('transaction-'.length);
    // Remove the type suffix (last "-{type}")
    const lastDashIndex = withoutPrefix.lastIndexOf('-');
    const offsetStr = withoutPrefix.substring(0, lastDashIndex);
    return parseInt(offsetStr, 10);
  }

  /**
   * トランザクションソートキーをAPIのパラメータに変換する関数
   * 月オフセットから絶対年月を計算し、compound sort_target文字列を返す
   * @param key - ヘッダーキー（例: "transaction--2-arrival"）
   * @param executionDate - 現在庫(nowInventory)の場合に年月を決定するためのExecutionDate
   * @returns - compound sort_target文字列（例: "transaction:arrivalAmount:2024:1"）
   */
  public static convertTransactionKeyToSortParameter(key: string, executionDate?: Date): string {
    const transactionType = InventoryTableService.extractTransactionType(key);

    const backendColumn = InventoryTableService.TRANSACTION_SORT_COLUMN_MAP[transactionType];
    if (!backendColumn) {
      return 'id'; // fallback
    }

    let targetYear: number;
    let targetMonth: number;

    if (transactionType === 'nowInventory' && executionDate) {
      // 現在庫はExecutionDateから年月を取得
      targetYear = executionDate.getFullYear();
      targetMonth = executionDate.getMonth() + 1;
    } else {
      // それ以外は月オフセットから絶対年月を計算（現在月基準）
      const monthOffset = InventoryTableService.extractMonthOffset(key);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // 1-based

      const totalMonths = currentYear * 12 + (currentMonth - 1) + monthOffset;
      targetYear = Math.floor(totalMonths / 12);
      targetMonth = (totalMonths % 12) + 1;
    }

    return `transaction:${backendColumn}:${targetYear}:${targetMonth}`;
  }
}
